/**
 * VÉRAULT Portal — Relational Picker
 *
 * A calm, unified picker for the relational spine that threads through
 * journals, media, and (soon) social posts. One component, three flavors:
 *
 *   <RelationalPicker
 *     kind="journey"            // 'journey' | 'destination' | 'hotel' | 'campaign' | 'media' | 'journal'
 *     value={[ids]}             // string[] of selected ids/slugs/tags
 *     onChange={(next) => …}
 *   />
 *
 * Behavior:
 *   · Typeahead with chip rendering of selections
 *   · Backed by an entity-aware loader (journeys from /api/travels/trips,
 *     destinations from /api/cms/destinations, campaigns from /api/cms/campaigns,
 *     media from /api/cms/media). For 'hotel' it acts as a free-text tag input.
 *   · Dropdown is keyboard-accessible (Enter to add, Backspace to remove last)
 *   · All selections live in a single string[] of stable ids/slugs/tags
 *
 * The component is intentionally not "smart" about cross-relation logic —
 * each instance owns one field. Page-level orchestration stays explicit.
 */
import React from "react";
import { X, ChevronDown, Plus } from "lucide-react";
import { api } from "../../portal/index";

const FETCHERS = {
  journey: async () => {
    const r = await api.get("/travels/trips").catch(() => ({ data: [] }));
    const arr = Array.isArray(r.data) ? r.data : [];
    return arr.map((t) => ({
      id: t.id,
      label: t.trip_title || t.internal_trip_name || t.client_trip_title || "Untitled trip",
      hint: [t.client_name, (t.destinations || []).join(", ")].filter(Boolean).join(" · "),
    }));
  },
  destination: async () => {
    const r = await api.get("/cms/destinations").catch(() => ({ data: [] }));
    const arr = Array.isArray(r.data) ? r.data : [];
    return arr.map((d) => ({
      id: (d.slug || "").toLowerCase(),
      label: d.destination_name || d.slug,
      hint: d.region || "",
    }));
  },
  campaign: async () => {
    const r = await api.get("/cms/campaigns").catch(() => ({ data: [] }));
    const arr = Array.isArray(r.data) ? r.data : [];
    return arr.map((c) => ({
      id: c.id,
      label: c.name,
      hint: c.public_visible ? "Public" : "Internal",
      color: c.color || "",
    }));
  },
  media: async () => {
    const r = await api.get("/cms/media", { params: { limit: 200 } }).catch(() => ({ data: [] }));
    const arr = Array.isArray(r.data) ? r.data : [];
    return arr.map((m) => ({
      id: m.id,
      label: m.original_filename || m.id,
      hint: [m.folder, m.destination_tag].filter(Boolean).join(" · "),
      thumb: m.media_type === "image" ? (process.env.REACT_APP_BACKEND_URL || "") + m.file_url : null,
    }));
  },
  journal: async () => {
    const r = await api.get("/cms/journal").catch(() => ({ data: [] }));
    const arr = Array.isArray(r.data) ? r.data : [];
    return arr.map((j) => ({
      id: j.id,
      label: j.title || "Untitled",
      hint: [j.category, j.destination_tag, j.status].filter(Boolean).join(" · "),
    }));
  },
};

const PLACEHOLDERS = {
  journey: "Link an experience…",
  destination: "Link a destination…",
  hotel: "Tag a hotel (free-text)…",
  campaign: "Assign a campaign…",
  media: "Attach media…",
  journal: "Link a journal entry…",
};

export function RelationalPicker({
  kind,
  value = [],
  onChange,
  testid,
  disabled = false,
  max = null,
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [options, setOptions] = React.useState(null); // null = loading
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);
  const wrapRef = React.useRef(null);

  // Load options for entity-backed kinds; hotel is free-text only.
  React.useEffect(() => {
    if (kind === "hotel") {
      setOptions([]); // no remote list
      return;
    }
    const loader = FETCHERS[kind];
    if (!loader) {
      setError(`Unsupported kind: ${kind}`);
      return;
    }
    let cancelled = false;
    setOptions(null);
    loader()
      .then((items) => {
        if (cancelled) return;
        setOptions(items);
      })
      .catch((e) => !cancelled && setError(e.message || "Couldn't load"));
    return () => {
      cancelled = true;
    };
  }, [kind]);

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selected = React.useMemo(() => {
    if (!options) return value.map((id) => ({ id, label: id, hint: "" }));
    const map = new Map(options.map((o) => [o.id, o]));
    return value.map((id) => map.get(id) || { id, label: id, hint: "" });
  }, [options, value]);

  const filtered = React.useMemo(() => {
    if (!options) return [];
    const term = q.trim().toLowerCase();
    const remaining = options.filter((o) => !value.includes(o.id));
    if (!term) return remaining.slice(0, 30);
    return remaining
      .filter(
        (o) =>
          o.label.toLowerCase().includes(term) ||
          (o.hint || "").toLowerCase().includes(term),
      )
      .slice(0, 30);
  }, [options, q, value]);

  const atMax = max !== null && value.length >= max;

  const add = (id) => {
    if (atMax || value.includes(id)) return;
    onChange([...value, id]);
    setQ("");
    inputRef.current?.focus();
  };

  const remove = (id) => {
    onChange(value.filter((x) => x !== id));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (kind === "hotel") {
        const v = q.trim().toLowerCase();
        if (v && !value.includes(v)) {
          add(v);
        }
        return;
      }
      if (filtered.length > 0) {
        add(filtered[0].id);
      }
    } else if (e.key === "Backspace" && !q && value.length > 0) {
      remove(value[value.length - 1]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      data-testid={testid || `relpicker-${kind}`}
    >
      <div
        className={`min-h-10 flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded transition-colors ${
          open ? "border-[hsl(var(--accent-gold))]" : "hover:border-[rgba(0,0,0,0.18)]"
        } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selected.map((s) => (
          <span
            key={s.id}
            className="inline-flex items-center gap-1 pl-2 pr-1 h-7 rounded-full bg-[hsl(var(--background-stone))]/70 border border-[rgba(0,0,0,0.06)] font-body text-[12px] text-[hsl(var(--foreground-secondary))]"
            data-testid={`relpicker-${kind}-chip-${s.id}`}
          >
            {s.color && (
              <span
                className="inline-block size-1.5 rounded-full"
                style={{ background: s.color }}
              />
            )}
            <span className="truncate max-w-[12rem]">{s.label}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                remove(s.id);
              }}
              data-testid={`relpicker-${kind}-remove-${s.id}`}
              className="size-4 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-white"
              aria-label="Remove"
            >
              <X className="size-3" strokeWidth={1.5} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={atMax ? "Limit reached" : selected.length ? "" : PLACEHOLDERS[kind]}
          disabled={atMax}
          data-testid={`relpicker-${kind}-input`}
          className="flex-1 min-w-[8rem] h-7 px-1 bg-transparent text-[13px] focus:outline-none placeholder:text-[hsl(var(--foreground-muted))]"
        />
        <ChevronDown
          className={`size-3.5 text-[hsl(var(--foreground-muted))] transition-transform ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={1.5}
        />
      </div>

      {open && !atMax && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 max-h-72 overflow-y-auto bg-[hsl(var(--background))] border border-[rgba(0,0,0,0.08)] rounded shadow-xl"
          data-testid={`relpicker-${kind}-dropdown`}
        >
          {kind === "hotel" ? (
            <button
              onClick={() => {
                const v = q.trim().toLowerCase();
                if (v) add(v);
              }}
              disabled={!q.trim()}
              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[hsl(var(--background-paper))] disabled:opacity-50 disabled:cursor-not-allowed text-left"
            >
              <Plus className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <span className="font-body text-[13px] text-[hsl(var(--foreground))]">
                {q.trim() ? (
                  <>
                    Tag <em className="font-accent text-[hsl(var(--accent-gold))]">"{q.trim()}"</em>
                  </>
                ) : (
                  <span className="text-[hsl(var(--foreground-muted))]">Type a hotel name and press Enter</span>
                )}
              </span>
            </button>
          ) : options === null ? (
            <div className="px-3 py-3 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
              Loading…
            </div>
          ) : error ? (
            <div className="px-3 py-3 font-body text-[12.5px] text-[hsl(var(--accent-terracotta))]">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-3 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
              {q ? `Nothing matches "${q}"` : "All set."}
            </div>
          ) : (
            <ul>
              {filtered.map((o) => (
                <li key={o.id}>
                  <button
                    onClick={() => add(o.id)}
                    data-testid={`relpicker-${kind}-option-${o.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[hsl(var(--background-paper))] text-left transition-colors"
                  >
                    {o.thumb ? (
                      <img
                        src={o.thumb}
                        alt=""
                        className="size-6 rounded object-cover shrink-0"
                      />
                    ) : (
                      o.color && (
                        <span
                          className="size-2 rounded-full shrink-0"
                          style={{ background: o.color }}
                        />
                      )
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block font-body text-[13px] text-[hsl(var(--foreground))] truncate">
                        {o.label}
                      </span>
                      {o.hint && (
                        <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] truncate">
                          {o.hint}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default RelationalPicker;
